import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { isCustomerViewEligible } from "../../hooks/useAlphaAccess";
import {
  emitAdminCustomerViewOwnerMismatch,
  getHttpStatus,
} from "./adminCustomerViewDiagnostics";
import {
  resolveAdminCustomerViewRouteSubject,
  type FrozenAdminCustomerViewRouteSubject,
} from "../../utils/adminCustomerViewRouteSession";
import { isValidPlutusUserId } from "../../utils/plutusUserId";
import usersService from "../../services/usersService";
import userConfigurationService from "../../services/UserConfigurationService";
import formService from "../../services/formService";
import {
  accountSummaryService,
  AdminAccountInfoVerificationError,
} from "../../services/accountSummaryService";
import { getAlphaProgressResponses } from "../alpha/alphaProgressResponses";
import AlphaDashboardPreviewPage from "../alpha/AlphaDashboardPreviewPage";
import AdminTargetPositionsAdapter from "../alpha/AdminTargetPositionsAdapter";
import AlphaProfilePage from "../alpha/AlphaProfilePage";
import type {
  TargetAccountState,
  TargetQuestionnaireState,
  VerifiedAdminTargetSubject,
} from "../alpha/alphaPageSubject";

export type AdminCustomerViewPageTab = "dashboard" | "positions" | "profile";

type PhaseAState =
  | { status: "idle" }
  | { status: "loading"; ownerUserId: string; generation: number }
  | { status: "ready"; subject: VerifiedAdminTargetSubject }
  | { status: "not-found"; generation: number }
  | { status: "invalid-id"; generation: number }
  | { status: "verification"; generation: number }
  | { status: "ineligible"; generation: number }
  | { status: "unavailable-role"; generation: number }
  | { status: "access"; generation: number }
  | { status: "unavailable"; generation: number };

let nextAdminCustomerViewGeneration = 0;

function allocateAdminCustomerViewGeneration(): number {
  nextAdminCustomerViewGeneration += 1;
  if (!Number.isSafeInteger(nextAdminCustomerViewGeneration)) {
    nextAdminCustomerViewGeneration = 1;
  }
  return nextAdminCustomerViewGeneration;
}

function shortenedUserId(userId: string): string {
  return `${userId.slice(0, 6)}...`;
}

function customerViewTabFromPathname(pathname: string): AdminCustomerViewPageTab | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length !== 4 || parts[0] !== "admin" || parts[1] !== "customer-view") {
    return null;
  }
  const page = parts[3];
  if (page === "dashboard" || page === "positions" || page === "profile") {
    return page;
  }
  return null;
}

function ShellFrame({
  viewKey,
  name,
  email,
  userId,
  children,
}: {
  viewKey: string;
  name: string;
  email: string;
  userId: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      data-testid="admin-customer-view-shell"
      className="min-h-dvh bg-v7-canvas transition-colors duration-300"
    >
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/80 px-6 py-4 backdrop-blur-md dark:border-rp-separator dark:bg-black/80">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Admin customer view - read-only
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                <span className="ph-mask font-medium text-gray-900 dark:text-white">{name}</span>
                <span className="mx-2 text-gray-400">|</span>
                <span className="ph-mask">{email}</span>
                <span className="mx-2 text-gray-400">|</span>
                <span className="font-mono">{shortenedUserId(userId)}</span>
              </p>
            </div>
            <a
              href="/admin/users"
              className="inline-flex min-h-10 items-center rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
            >
              Back to user
            </a>
          </div>
          <nav aria-label="Customer view pages" className="flex gap-2">
            {([
              ["dashboard", "Dashboard"],
              ["positions", "Positions"],
              ["profile", "Profile"],
            ] as const).map(([tab, label]) => (
              <NavLink
                key={tab}
                to={`/admin/customer-view/${viewKey}/${tab}`}
                className={({ isActive }) =>
                  `rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-rp-amber text-white"
                      : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="bg-v7-canvas p-6 font-v7-sans text-v7-ink md:p-8">
        <div className="mx-auto max-w-[1400px]">
          {children}
        </div>
      </main>
    </div>
  );
}

function UnavailableView(): React.ReactElement {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-50 p-6 dark:bg-black">
      <section className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-8 dark:border-rp-separator dark:bg-rp-surface">
        <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
          Customer View link unavailable
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Return to Users and open Customer View again.
        </p>
        <a
          href="/admin/users"
          className="mt-6 inline-flex min-h-10 items-center rounded-lg bg-rp-amber px-4 text-sm font-semibold text-white hover:bg-yellow-600"
        >
          Back to Users
        </a>
      </section>
    </div>
  );
}

export default function AdminCustomerViewPage(): React.ReactElement {
  const { viewKey } = useParams<{ viewKey: string }>();
  const location = useLocation();
  const page = customerViewTabFromPathname(location.pathname);
  const { user: actor, isLoading: authLoading } = useAuth();
  const actorId = actor?.id ?? null;

  if (!page) {
    return <Outlet />;
  }

  return (
    <AdminCustomerViewRouteLoader
      key={`${authLoading ? "loading" : "ready"}:${actorId ?? "anonymous"}:${viewKey ?? "missing"}`}
      viewKey={viewKey}
      page={page}
      actorId={actorId}
      authLoading={authLoading}
    />
  );
}

function AdminCustomerViewRouteLoader({
  viewKey,
  page,
  actorId,
  authLoading,
}: {
  viewKey: string | undefined;
  page: AdminCustomerViewPageTab;
  actorId: string | null;
  authLoading: boolean;
}): React.ReactElement {
  const [routeGeneration] = useState(() => ({
    generation: allocateAdminCustomerViewGeneration(),
    frozen: !authLoading && actorId && viewKey
      ? resolveAdminCustomerViewRouteSubject(viewKey, actorId)
      : null,
  }));
  const frozen: FrozenAdminCustomerViewRouteSubject | null = routeGeneration.frozen;
  const generation = routeGeneration.generation;
  const loadedQuestionnaireGenerationRef = useRef<number | null>(null);
  const loadedAccountGenerationRef = useRef<number | null>(null);
  const [phaseA, setPhaseA] = useState<PhaseAState>({ status: "idle" });
  const [questionnaire, setQuestionnaire] = useState<TargetQuestionnaireState | null>(null);
  const [accountState, setAccountState] = useState<TargetAccountState | null>(null);
  const phaseAReqRef = useRef(0);
  const questionnaireReqRef = useRef(0);
  const accountReqRef = useRef(0);
  const accountFlightRef = useRef<{
    generation: number;
    promise: Promise<void>;
  } | null>(null);

  useEffect(() => {
    if (authLoading || !actorId || !viewKey) return;
    if (!frozen) return;
    const reqId = ++phaseAReqRef.current;
    const targetUserId = frozen.targetUserId;
    if (!isValidPlutusUserId(targetUserId)) {
      setPhaseA({ status: "invalid-id", generation });
      return;
    }
    let active = true;
    const isCurrentRequest = () => active && reqId === phaseAReqRef.current;
    setPhaseA({ status: "loading", ownerUserId: targetUserId, generation });
    void (async () => {
      try {
        let user;
        try {
          user = await usersService.getUserById(targetUserId);
        } catch (error) {
          if (!isCurrentRequest()) return;
          const status = getHttpStatus(error);
          if (status === 404) {
            setPhaseA({ status: "not-found", generation });
            return;
          }
          if (status === 401 || status === 403) {
            setPhaseA({ status: "access", generation });
            return;
          }
          setPhaseA({ status: "verification", generation });
          return;
        }
        if (!isCurrentRequest()) return;
        let config;
        try {
          config = await userConfigurationService.getUserConfiguration(targetUserId);
        } catch (error) {
          if (!isCurrentRequest()) return;
          const status = getHttpStatus(error);
          if (status === 404) {
            setPhaseA({ status: "unavailable", generation });
            return;
          }
          if (status === 401 || status === 403) {
            setPhaseA({ status: "access", generation });
            return;
          }
          setPhaseA({ status: "verification", generation });
          return;
        }
        if (!isCurrentRequest()) return;
        if (user.id !== targetUserId) {
          emitAdminCustomerViewOwnerMismatch("user");
          setPhaseA({ status: "verification", generation });
          return;
        }
        if (config.userId !== targetUserId) {
          emitAdminCustomerViewOwnerMismatch("configuration");
          setPhaseA({ status: "verification", generation });
          return;
        }
        const roles = user.roles ?? [];
        if (!isCustomerViewEligible(roles, config.data?.iscustomer)) {
          const hasRole = roles.includes("ADMIN") || roles.includes("INVESTOR") || roles.length === 0;
          setPhaseA({
            status: hasRole ? "ineligible" : "unavailable-role",
            generation,
          });
          return;
        }
        setPhaseA({
          status: "ready",
          subject: {
            userId: targetUserId,
            user,
            userConfig: config,
            generation,
          },
        });
      } catch {
        if (!isCurrentRequest()) return;
        setPhaseA({ status: "verification", generation });
      }
    })();
    return () => {
      active = false;
    };
  }, [authLoading, actorId, viewKey, frozen, generation]);

  const loadQuestionnaire = useCallback(async (subject: VerifiedAdminTargetSubject) => {
    const reqId = ++questionnaireReqRef.current;
    setQuestionnaire({
      status: "loading",
      ownerUserId: subject.userId,
      generation: subject.generation,
    });
    try {
      const [questions, responses] = await Promise.all([
        formService.listQuestions(),
        formService.getResponsesForUser(subject.userId),
      ]);
      if (reqId !== questionnaireReqRef.current) return;
      if (responses.some((row) => row.userId !== subject.userId)) {
        emitAdminCustomerViewOwnerMismatch("responses");
        setQuestionnaire({
          status: "failed",
          ownerUserId: subject.userId,
          generation: subject.generation,
        });
        return;
      }
      setQuestionnaire({
        status: "loaded",
        ownerUserId: subject.userId,
        generation: subject.generation,
        progressResponses: getAlphaProgressResponses(questions, responses),
      });
    } catch {
      if (reqId !== questionnaireReqRef.current) return;
      setQuestionnaire({
        status: "failed",
        ownerUserId: subject.userId,
        generation: subject.generation,
      });
    }
  }, []);

  const loadAccount = useCallback((subject: VerifiedAdminTargetSubject): Promise<void> => {
    const existing = accountFlightRef.current;
    if (existing?.generation === subject.generation) {
      return existing.promise;
    }

    const reqId = ++accountReqRef.current;
    const request = (async () => {
      setAccountState({
        status: "loading",
        ownerUserId: subject.userId,
        generation: subject.generation,
      });
      try {
        const owner = await accountSummaryService.getAdminUserAccountInfoOwner(subject.userId);
        if (reqId !== accountReqRef.current) return;
        if (owner.userId !== subject.userId) {
          emitAdminCustomerViewOwnerMismatch("account-info");
          setPhaseA({ status: "verification", generation: subject.generation });
          return;
        }
        setAccountState({
          status: "linked",
          ownerUserId: subject.userId,
          generation: subject.generation,
        });
      } catch (error) {
        if (reqId !== accountReqRef.current) return;
        if (error instanceof AdminAccountInfoVerificationError) {
          setPhaseA({ status: "verification", generation: subject.generation });
          return;
        }
        const status = getHttpStatus(error);
        if (status === 404) {
          setAccountState({
            status: "none",
            ownerUserId: subject.userId,
            generation: subject.generation,
          });
          return;
        }
        if (status === 401 || status === 403) {
          setPhaseA({ status: "access", generation: subject.generation });
          return;
        }
        setAccountState({
          status: "failed",
          ownerUserId: subject.userId,
          generation: subject.generation,
        });
      }
    })();
    accountFlightRef.current = { generation: subject.generation, promise: request };
    void request.finally(() => {
      if (accountFlightRef.current?.promise === request) {
        accountFlightRef.current = null;
      }
    });
    return request;
  }, []);

  useEffect(() => {
    if (phaseA.status !== "ready") {
      loadedQuestionnaireGenerationRef.current = null;
      loadedAccountGenerationRef.current = null;
      setQuestionnaire(null);
      setAccountState(null);
      return;
    }
    const generation = phaseA.subject.generation;
    if (page === "dashboard" || page === "profile") {
      if (loadedQuestionnaireGenerationRef.current !== generation) {
        loadedQuestionnaireGenerationRef.current = generation;
        void loadQuestionnaire(phaseA.subject);
      }
    } else {
      loadedQuestionnaireGenerationRef.current = null;
      setQuestionnaire(null);
    }
    if (page === "dashboard" || page === "positions") {
      if (loadedAccountGenerationRef.current !== generation) {
        loadedAccountGenerationRef.current = generation;
        void loadAccount(phaseA.subject);
      }
    } else {
      loadedAccountGenerationRef.current = null;
      setAccountState(null);
    }
  }, [phaseA, page, loadQuestionnaire, loadAccount]);

  if (authLoading || !actorId) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-black">
        <div className="h-8 w-8 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
      </div>
    );
  }

  if (!viewKey || !frozen) {
    return <UnavailableView />;
  }

  const phaseAGeneration = phaseA.status === "idle"
    ? generation
    : phaseA.status === "ready"
      ? phaseA.subject.generation
      : phaseA.generation;
  if (phaseAGeneration !== generation) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-black">
        <div className="h-8 w-8 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
      </div>
    );
  }

  if (phaseA.status === "invalid-id") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 p-6 dark:bg-black">
        <section className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-8 dark:border-rp-separator dark:bg-rp-surface">
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            Invalid customer route
          </h1>
        </section>
      </div>
    );
  }

  if (phaseA.status === "not-found") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 p-6 dark:bg-black">
        <section className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-8 dark:border-rp-separator dark:bg-rp-surface">
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            Customer not found
          </h1>
        </section>
      </div>
    );
  }

  if (phaseA.status === "unavailable") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 p-6 dark:bg-black">
        <section className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-8 dark:border-rp-separator dark:bg-rp-surface">
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            Customer View unavailable
          </h1>
        </section>
      </div>
    );
  }

  if (phaseA.status === "ineligible") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 p-6 dark:bg-black">
        <section className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-8 dark:border-rp-separator dark:bg-rp-surface">
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            Not an active customer
          </h1>
        </section>
      </div>
    );
  }

  if (phaseA.status === "unavailable-role") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 p-6 dark:bg-black">
        <section className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-8 dark:border-rp-separator dark:bg-rp-surface">
          <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            Unavailable for role
          </h1>
        </section>
      </div>
    );
  }

  if (phaseA.status === "verification" || phaseA.status === "access") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 p-6 dark:bg-black">
        <section className="w-full max-w-lg rounded-xl border border-red-200 bg-red-50 p-8 dark:border-red-800 dark:bg-red-900/20">
          <h1 className="text-xl font-bold tracking-tight text-red-700 dark:text-red-400">
            Customer View could not be verified
          </h1>
        </section>
      </div>
    );
  }

  if (phaseA.status !== "ready") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-black">
        <div className="h-8 w-8 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
      </div>
    );
  }

  const subject = phaseA.subject;
  if (
    subject.generation !== generation
    || subject.userId !== frozen.targetUserId
    || subject.user.id !== frozen.targetUserId
    || subject.userConfig.userId !== frozen.targetUserId
  ) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 p-6 dark:bg-black">
        <section className="w-full max-w-lg rounded-xl border border-red-200 bg-red-50 p-8 dark:border-red-800 dark:bg-red-900/20">
          <h1 className="text-xl font-bold tracking-tight text-red-700 dark:text-red-400">
            Customer View could not be verified
          </h1>
        </section>
      </div>
    );
  }
  const displayName = subject.user.name || subject.user.email;
  const questionnaireState: TargetQuestionnaireState = questionnaire
    && questionnaire.ownerUserId === subject.userId
    && questionnaire.generation === subject.generation
    ? questionnaire
    : {
    status: "loading",
    ownerUserId: subject.userId,
    generation: subject.generation,
  };
  const account: TargetAccountState = accountState
    && accountState.ownerUserId === subject.userId
    && accountState.generation === subject.generation
    ? accountState
    : {
    status: "loading",
    ownerUserId: subject.userId,
    generation: subject.generation,
  };

  return (
    <ShellFrame
      viewKey={frozen.routeSubjectKey}
      name={displayName}
      email={subject.user.email}
      userId={subject.userId}
    >
      <AdminCustomerViewExperience
        key={`${frozen.routeSubjectKey}:${subject.userId}`}
        page={page}
        subject={subject}
        questionnaire={questionnaireState}
        accountState={account}
        onRetryQuestionnaire={() => void loadQuestionnaire(subject)}
        onRetryAccountPreflight={() => void loadAccount(subject)}
      />
    </ShellFrame>
  );
}

function AdminCustomerViewExperience({
  page,
  subject,
  questionnaire,
  accountState,
  onRetryQuestionnaire,
  onRetryAccountPreflight,
}: {
  page: AdminCustomerViewPageTab;
  subject: VerifiedAdminTargetSubject;
  questionnaire: TargetQuestionnaireState;
  accountState: TargetAccountState;
  onRetryQuestionnaire: () => void;
  onRetryAccountPreflight: () => void;
}): React.ReactElement {
  if (page === "dashboard") {
    return (
      <AlphaDashboardPreviewPage
        mode="admin-target"
        subject={subject}
        questionnaire={questionnaire}
        accountState={accountState}
        onRetryQuestionnaire={onRetryQuestionnaire}
        onRetryAccountPreflight={onRetryAccountPreflight}
      />
    );
  }
  if (page === "positions") {
    return (
      <AdminTargetPositionsAdapter
        subject={subject}
        accountState={accountState}
        onRetryAccountPreflight={onRetryAccountPreflight}
      />
    );
  }
  return (
    <AlphaProfilePage
      mode="admin-target"
      subject={subject}
      questionnaire={questionnaire}
      onRetryQuestionnaire={onRetryQuestionnaire}
    />
  );
}
